import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../../styles';
import { getInsights } from '../../../apis/backend';
import * as stores from '../../../stores';
import externalLink from '../../../assets/externalLink.svg';
import exceptionalIcon from '../../RatingIndicator/defaultImages/exceptional.svg';
import unfavorableIcon from '../../RatingIndicator/defaultImages/unfavorable.svg';

interface Props {
  symbol: string;
}

const ALLOWED_FREE_SYMBOLS = ['AAPL', 'TSLA'];

const AllInsightsPage: FC<Props> = ({ symbol }) => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle subscription check
  useEffect(() => {
    if (!isSubscribed && !ALLOWED_FREE_SYMBOLS.includes(symbol.toUpperCase())) {
      window.scrollTo(0, 0);
      stores.showPaymentModalByPreference();
      // Use a deterministic fallback route instead of goBack() so deep links
      // without browser history never end up on a blank screen.
      history.replace(`/symbol/${symbol}`);
    }
  }, [isSubscribed, symbol, history]);

  const { data, isLoading } = useQuery(['investments', symbol, 'insights', i18n.language], async () => {
    const response = await getInsights(symbol);
    return Array.isArray(response) ? response : [];
  });

  if (isLoading || !data?.length) return null;

  // Sort insights by date (most recent first)
  const sortedInsights = [...data].sort(
    (a, b) => new Date(b.articleDate).getTime() - new Date(a.articleDate).getTime(),
  );

  return (
    <div>
      <h2 css={styles.heading}>Insights</h2>

      {sortedInsights.map((insight, insightIndex) => {
        const date = new Date(insight.articleDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <div key={insightIndex} css={[styles.insightContainer, insightIndex > 0 && styles.insightSeparator]}>
            <div css={styles.titleRow}>
              <a href={insight.fileUrl} target="_blank" rel="noopener noreferrer" css={styles.titleLink}>
                {insight.title}
                <img src={externalLink} css={styles.externalLinkIcon} alt="" />
              </a>
              <span css={styles.date}>{date}</span>
            </div>

            <p css={styles.summary}>{insight.summary}</p>

            <div css={styles.section}>
              {insight.keyPoints.map((point, index) => (
                <a
                  key={index}
                  href={`${insight.fileUrl}#page=${point.page}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  css={styles.pointLink}
                >
                  <div css={styles.point}>
                    <div css={styles.pointHeader}>
                      <img src={exceptionalIcon} css={styles.pointIcon} alt="" />
                      <div css={styles.pageNumber}>Page {point.page}</div>
                    </div>
                    <div css={styles.pointText}>{point.details}</div>
                  </div>
                </a>
              ))}
            </div>

            <div css={styles.section}>
              {insight.potentialRisks.map((risk, index) => (
                <a
                  key={index}
                  href={`${insight.fileUrl}#page=${risk.page}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  css={styles.pointLink}
                >
                  <div css={styles.point}>
                    <div css={styles.pointHeader}>
                      <img src={unfavorableIcon} css={styles.pointIcon} alt="" />
                      <div css={styles.pageNumber}>Page {risk.page}</div>
                    </div>
                    <div css={styles.pointText}>{risk.details}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  heading: css`
    margin: 0 0 ${spacing.centi} 0;
    ${fontStyles.shark}
  `,
  titleRow: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${spacing.deci};
  `,
  titleLink: css`
    color: ${colors.petrolBlue};
    text-decoration: none;
    ${fontStyles.roboHorse}
  `,
  externalLinkIcon: css`
    vertical-align: text-bottom;
    width: 1.25rem;
    height: 1.25rem;
    margin-left: ${spacing.milli};
  `,
  date: css`
    color: ${colors.silver};
    ${fontStyles.roboCat}

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  summary: css`
    margin: ${spacing.deci} 0;
    color: ${colors.tungsten};
    ${fontStyles.cat}

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  section: css`
    margin-top: ${spacing.centi};
  `,
  pointLink: css`
    text-decoration: none;
    display: block;

    &:hover {
      text-decoration: none;
    }
  `,
  point: css`
    margin-bottom: ${spacing.centi};
    padding: ${spacing.centi};
    border-radius: 8px;
    background: ${colors.fog};

    html[data-theme='dark'] & {
      background: #000000;
    }

    &:hover {
      background: ${colors.snow};

      html[data-theme='dark'] & {
        background: ${colors.silver};
      }
    }
  `,
  pointHeader: css`
    display: flex;
    align-items: center;
    gap: ${spacing.milli};
    margin-bottom: ${spacing.milli};
  `,
  pointIcon: css`
    width: 16px;
    height: 16px;
  `,
  pageNumber: css`
    color: ${colors.petrolBlue};
    ${fontStyles.mouse}

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
  pointText: css`
    ${fontStyles.cat}
    color: ${colors.liquorice};

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  insightContainer: css`
    margin-top: ${spacing.regular};
    padding-top: ${spacing.regular};

    &:first-of-type {
      margin-top: ${spacing.centi};
      padding-top: ${spacing.centi};
    }
  `,
  insightSeparator: css`
    border-top: 1px solid ${colors.cloud};
  `,
};

export default AllInsightsPage;
